import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface KeyHistoryEntry {
  id: string;
  tenant_id: string | null;
  key_version: number;
  key_hash: string;
  rotated_at: string;
  rotated_by: string | null;
  rotated_by_email: string | null; // Can be null if rotated by system or user deleted
  reason: string | null;
  is_active: boolean;
  created_at: string;
}

interface CurrentKeyInfo {
  key_version: number;
  key_hash_masked: string;
  rotated_at: string;
  rotated_by_email: string; // Always non-null due to COALESCE in SQL
  days_since_rotation: number;
}

interface RotateKeyParams {
  tenant_id?: string | null;
  key_hash: string;
  reason?: string;
}

interface VerifyKeyParams {
  tenant_id?: string | null;
  key_hash: string;
}

export function useCurrentKeyInfo(tenantId?: string | null) {
  return useQuery({
    queryKey: ["encryption-key-current", tenantId],
    queryFn: async (): Promise<CurrentKeyInfo | null> => {
      const { data, error } = await supabase.rpc("calie_admin_get_current_key_info", {
        p_tenant_id: tenantId ?? null,
      });

      if (error) {
        console.error("Error fetching current key info:", error);
        throw error;
      }

      // RPC returns an array, get first item
      const result = Array.isArray(data) && data.length > 0 ? data[0] : null;
      return result as CurrentKeyInfo | null;
    },
  });
}

export function useKeyHistory(tenantId?: string | null, limit: number = 50) {
  return useQuery({
    queryKey: ["encryption-key-history", tenantId, limit],
    queryFn: async (): Promise<KeyHistoryEntry[]> => {
      const { data, error } = await supabase.rpc("calie_admin_get_key_history", {
        p_tenant_id: tenantId ?? null,
        p_limit: limit,
      });

      if (error) {
        console.error("Error fetching key history:", error);
        throw error;
      }

      return (data || []) as KeyHistoryEntry[];
    },
  });
}

export function useRotateKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: RotateKeyParams): Promise<number> => {
      const { data, error } = await supabase.rpc("calie_admin_rotate_encryption_key", {
        p_tenant_id: params.tenant_id ?? null,
        p_new_key_hash: params.key_hash,
        p_reason: params.reason ?? null,
      });

      if (error) {
        console.error("Error rotating key:", error);
        throw error;
      }

      return data as number;
    },
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ["encryption-key-current", variables.tenant_id] });
      queryClient.invalidateQueries({ queryKey: ["encryption-key-history", variables.tenant_id] });
    },
  });
}

export function useVerifyKey() {
  return useMutation({
    mutationFn: async (params: VerifyKeyParams): Promise<boolean> => {
      const { data, error } = await supabase.rpc("calie_admin_verify_encryption_key", {
        p_tenant_id: params.tenant_id ?? null,
        p_key_hash: params.key_hash,
      });

      if (error) {
        console.error("Error verifying key:", error);
        throw error;
      }

      return data as boolean;
    },
  });
}

// Utility function to generate SHA-256 hash of a key
export async function hashKey(key: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(key);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  return hashHex;
}
